import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { ColorPicker } from '@/components/ui/color-picker';

interface LayoutControlsProps {
  plotlyJson?: any;
  onUpdate: (updateType: string, value: any) => void;
}

export function LayoutControls({ plotlyJson, onUpdate }: LayoutControlsProps) {
  const widthPx = useMemo(() => plotlyJson?.layout?.width ?? 800, [plotlyJson?.layout?.width]);
  const heightPx = useMemo(() => plotlyJson?.layout?.height ?? 600, [plotlyJson?.layout?.height]);
  const margin = useMemo(() => ({
    t: plotlyJson?.layout?.margin?.t ?? 80,
    r: plotlyJson?.layout?.margin?.r ?? 80,
    b: plotlyJson?.layout?.margin?.b ?? 80,
    l: plotlyJson?.layout?.margin?.l ?? 80,
  }), [plotlyJson?.layout?.margin?.t, plotlyJson?.layout?.margin?.r, plotlyJson?.layout?.margin?.b, plotlyJson?.layout?.margin?.l]);

  // Helper to convert rgba/hex to opaque hex
  const toOpaqueHex = (input?: string, fallback = '#ffffff') => {
    try {
      const c = (input || '').toString().trim();
      if (!c) return fallback;
      const rgba = c.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-1]?(?:\.\d+)?))?\)/i);
      if (rgba) {
        const r = Math.max(0, Math.min(255, parseInt(rgba[1], 10)));
        const g = Math.max(0, Math.min(255, parseInt(rgba[2], 10)));
        const b = Math.max(0, Math.min(255, parseInt(rgba[3], 10)));
        const toHex = (n: number) => n.toString(16).padStart(2, '0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      }
      if (/^#([0-9a-fA-F]{8})$/.test(c)) return `#${c.slice(1, 7)}`;
      if (/^#([0-9a-fA-F]{6})$/.test(c)) return c;
      return fallback;
    } catch {
      return fallback;
    }
  };

  return (
    <div className="space-y-3">
      {/* Size & Margins */}
      <div>
        <Label className="text-sm text-muted-foreground">Plot size</Label>
        <div className="space-y-3 mt-1">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground">Width</Label>
              <span className="text-[11px] text-muted-foreground">{widthPx}px</span>
            </div>
            <Slider defaultValue={[widthPx]} min={200} max={2000} step={10} onValueChange={([v]) => onUpdate('layout.width', v)} />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground">Height</Label>
              <span className="text-[11px] text-muted-foreground">{heightPx}px</span>
            </div>
            <Slider defaultValue={[heightPx]} min={200} max={2000} step={10} onValueChange={([v]) => onUpdate('layout.height', v)} />
          </div>
        </div>
      </div>

      {/* Preview Zoom (Editor Only) */}
      <div className="space-y-2 pt-2 border-t">
        <div className="flex items-center justify-between">
          <Label className="text-sm text-muted-foreground">Preview zoom (editor only)</Label>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-muted-foreground">Scale</Label>
            <span className="text-[11px] text-muted-foreground">{Math.round(((plotlyJson?.layout as any)?._previewScale ?? 1) * 100)}%</span>
          </div>
          <Slider
            value={[((plotlyJson?.layout as any)?._previewScale ?? 1)]}
            min={0.5}
            max={3}
            step={0.1}
            onValueChange={([v]) => onUpdate('design.preview.scale', v)}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Tip: You can drag the plot title and legend directly on the canvas to reposition them.
          </p>
        </div>
      </div>

      {/* Margins */}
      <div>
        <Label className="text-sm text-muted-foreground">Margins</Label>
        <div className="grid grid-cols-2 gap-4 mt-1">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground">Top</Label>
              <span className="text-[11px] text-muted-foreground">{margin.t}px</span>
            </div>
            <Slider defaultValue={[margin.t]} min={0} max={200} step={2} onValueChange={([v]) => onUpdate('layout.margin.t', v)} />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground">Right</Label>
              <span className="text-[11px] text-muted-foreground">{margin.r}px</span>
            </div>
            <Slider defaultValue={[margin.r]} min={0} max={200} step={2} onValueChange={([v]) => onUpdate('layout.margin.r', v)} />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground">Bottom</Label>
              <span className="text-[11px] text-muted-foreground">{margin.b}px</span>
            </div>
            <Slider defaultValue={[margin.b]} min={0} max={200} step={2} onValueChange={([v]) => onUpdate('layout.margin.b', v)} />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground">Left</Label>
              <span className="text-[11px] text-muted-foreground">{margin.l}px</span>
            </div>
            <Slider defaultValue={[margin.l]} min={0} max={200} step={2} onValueChange={([v]) => onUpdate('layout.margin.l', v)} />
          </div>
        </div>
      </div>

      {/* Label Margins */}
      <div>
        <Label className="text-sm text-muted-foreground">Label margin adjustment</Label>
        <Slider
          defaultValue={[0]}
          min={-50}
          max={50}
          step={5}
          className="mt-1"
          onValueChange={([v]) => onUpdate('layout.margin.pad', v)}
        />
      </div>

      {/* Background Colors */}
      <div className="pt-2 border-t">
        <Label className="text-sm text-muted-foreground">Background</Label>
        <div className="grid grid-cols-2 gap-4 mt-1">
          <div>
            <Label className="text-sm text-muted-foreground">Paper</Label>
            <ColorPicker
              value={toOpaqueHex(plotlyJson?.layout?.paper_bgcolor, '#ffffff')}
              onChange={(color) => onUpdate('design.bgcolor', color)}
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground">Plot area</Label>
            <ColorPicker
              value={toOpaqueHex(plotlyJson?.layout?.plot_bgcolor, '#ffffff')}
              onChange={(color) => onUpdate('design.plotbgcolor', color)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
